import React, { useState } from 'react';
import {
  Box, IconButton, InputBase, Typography, useTheme,
} from '@mui/material';
import { deepPurple } from '@mui/material/colors';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { TestIds } from '../../../assets/testIds';
import { COACH_ROUTE } from '../../../routes/namedRoutes';
import { useCustomEventTracking } from '../../../common/useCustomEventTracking';
import type { CoachLocationState } from '../../Coach/Coach';

const iconBadgeSx = {
  width: 25,
  height: 25,
  borderRadius: '50%',
  backgroundColor: deepPurple[500],
  color: 'common.white',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

/**
 * Pill on the home page for asking the AI Business Coach a question (SN-1046).
 * Submitting opens the AI Coach tab, which starts a new chat with the question.
 */
export function HomeCoachBar() {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { trackEngagement } = useCustomEventTracking();
  const [question, setQuestion] = useState('');

  const handleSubmit = (event?: React.FormEvent) => {
    event?.preventDefault();
    const trimmed = question.trim();
    if (!trimmed) return;
    // Length only: the question itself stays out of analytics, as on the Coaching page.
    trackEngagement('Home', 'Click', 'CoachBarSubmit', { questionLength: trimmed.length });
    const state: CoachLocationState = { initialQuestion: trimmed };
    navigate(`${COACH_ROUTE}#aicoach`, { state });
  };

  return (
    <Box
      data-testid={TestIds.homePage.coachBar}
      sx={{
        backgroundColor: deepPurple[50],
        padding: `${theme.spacing(1.25)} ${theme.spacing(3)}`,
      }}
    >
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          display: 'flex',
          alignItems: 'center',
          minHeight: 44,
          padding: `${theme.spacing(0.75)} ${theme.spacing(0.5)}`,
          backgroundColor: theme.palette.background.paper,
          border: `1.5px solid ${deepPurple[100]}`,
          borderRadius: 100,
          // Focus indicator for the whole pill while the input is focused (per Figma)
          '&:focus-within': {
            borderColor: deepPurple[500],
            boxShadow: `0 0 8px 0 ${deepPurple[200]}`,
          },
        }}
      >
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing(1.25),
          paddingLeft: theme.spacing(1.25),
          paddingRight: theme.spacing(2),
        }}>
          <Box sx={iconBadgeSx}>
            <AutoAwesomeIcon sx={{ fontSize: 12 }} />
          </Box>
          <Typography sx={{
            fontSize: 15,
            fontWeight: 500,
            letterSpacing: '0.15px',
            color: deepPurple[500],
            whiteSpace: 'nowrap',
            [theme.breakpoints.down('sm')]: { display: 'none' },
          }}>
            {t('homePage.coachBar.label')}
          </Typography>
        </Box>
        <Box sx={{
          width: '1px',
          height: 32,
          backgroundColor: deepPurple[100],
          [theme.breakpoints.down('sm')]: { display: 'none' },
        }} />
        <InputBase
          value={question}
          onChange={(event) => setQuestion(event.target.value)}
          placeholder={`${t('homePage.coachBar.placeholder')}`}
          inputProps={{
            'aria-label': `${t('homePage.coachBar.placeholder')}`,
            'data-testid': TestIds.homePage.coachInput,
          }}
          sx={{
            flex: 1,
            paddingLeft: theme.spacing(2.5),
            paddingRight: theme.spacing(2),
            ...theme.typography.body2,
          }}
        />
        <IconButton
          type="submit"
          aria-label={`${t('homePage.coachBar.send')}`}
          data-testid={TestIds.homePage.coachSendBtn}
          disabled={!question.trim()}
          sx={{
            ...iconBadgeSx,
            marginRight: theme.spacing(1.25),
            '&:hover': { backgroundColor: deepPurple[700] },
            '&.Mui-disabled': { backgroundColor: deepPurple[500], color: 'common.white', opacity: 0.5 },
          }}
        >
          <ArrowUpwardIcon sx={{ fontSize: 12 }} />
        </IconButton>
      </Box>
    </Box>
  );
}
